var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckCircle as CheckCircleIcon, Delete as DeleteIcon, Error as ErrorIcon, Visibility, VisibilityOff, } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, InputAdornment, TextField, Typography, } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../services/api";
export const OpenAISettings = () => {
    const { getAccessTokenSilently } = useAuth0();
    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);
    const [apiKeys, setApiKeys] = useState(null);
    const [openaiKey, setOpenaiKey] = useState("");
    const [showOpenaiKey, setShowOpenaiKey] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [testResults, setTestResults] = useState({});
    const loadApiKeys = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const token = yield getAccessTokenSilently();
            const data = yield apiService.getApiKeys(token);
            setApiKeys(data);
            setLoading(false);
        }
        catch (error) {
            console.error("Failed to load API keys:", error);
            setErrorMessage("Failed to load API keys");
            setLoading(false);
        }
    }), [getAccessTokenSilently]);
    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);
    const handleSaveKeys = () => __awaiter(void 0, void 0, void 0, function* () {
        setSaving(true);
        setSuccessMessage("");
        setErrorMessage("");
        try {
            const token = yield getAccessTokenSilently();
            const updateData = {
                aiProvider: "openai",
            };
            if (openaiKey) {
                updateData.openaiApiKey = openaiKey;
            }
            yield apiService.updateApiKeys(token, updateData);
            setSuccessMessage("API key saved successfully");
            yield loadApiKeys();
            setOpenaiKey("");
            setShowOpenaiKey(false);
        }
        catch (error) {
            console.error("Failed to save API keys:", error);
            setErrorMessage(error instanceof Error ? error.message : "Failed to save API keys");
        }
        finally {
            setSaving(false);
        }
    });
    const handleTestKey = () => __awaiter(void 0, void 0, void 0, function* () {
        setTesting("openai");
        setTestResults({});
        try {
            const token = yield getAccessTokenSilently();
            if (!openaiKey) {
                setTestResults({ openai: { valid: false, error: "Please enter an API key" } });
                setTesting(null);
                return;
            }
            const result = yield apiService.testApiKey(token, "openai", openaiKey);
            setTestResults({ openai: result });
        }
        catch (error) {
            console.error("Failed to test OpenAI API key:", error);
            setTestResults({
                openai: {
                    valid: false,
                    error: error instanceof Error ? error.message : "Test failed",
                },
            });
        }
        finally {
            setTesting(null);
        }
    });
    const handleDeleteKey = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!window.confirm("Are you sure you want to delete your OpenAI API key?")) {
            return;
        }
        try {
            const token = yield getAccessTokenSilently();
            yield apiService.deleteApiKey(token, "openai");
            setSuccessMessage("OpenAI API key deleted successfully");
            yield loadApiKeys();
        }
        catch (error) {
            console.error("Failed to delete OpenAI API key:", error);
            setErrorMessage(error instanceof Error ? error.message : "Failed to delete OpenAI API key");
        }
    });
    if (loading) {
        return (_jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" } }, { children: _jsx(CircularProgress, {}) })));
    }
    return (_jsxs(Box, Object.assign({ sx: { maxWidth: 900 } }, { children: [successMessage && (_jsx(Alert, Object.assign({ severity: "success", sx: { mb: 3 }, onClose: () => setSuccessMessage("") }, { children: successMessage }))), errorMessage && (_jsx(Alert, Object.assign({ severity: "error", sx: { mb: 3 }, onClose: () => setErrorMessage("") }, { children: errorMessage }))), _jsx(Card, Object.assign({ sx: { mb: 3 } }, { children: _jsxs(CardContent, { children: [_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 } }, { children: [_jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "OpenAI API Key" })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "Configure your own OpenAI API key." }))] }), (apiKeys === null || apiKeys === void 0 ? void 0 : apiKeys.hasOpenaiKey) && (_jsx(Chip, { label: "Configured", color: "success", size: "small", icon: _jsx(CheckCircleIcon, {}) }))] })), (apiKeys === null || apiKeys === void 0 ? void 0 : apiKeys.hasOpenaiKey) && (_jsx(Box, Object.assign({ sx: { mb: 2 } }, { children: _jsxs(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: ["Current key: ", _jsx("code", { children: apiKeys.openaiKeyMasked })] })) }))), _jsx(TextField, { fullWidth: true, label: "OpenAI API Key", type: showOpenaiKey ? "text" : "password", value: openaiKey, onChange: (e) => setOpenaiKey(e.target.value), placeholder: "sk-proj-...", sx: { mb: 2 }, InputProps: {
                                endAdornment: (_jsx(InputAdornment, Object.assign({ position: "end" }, { children: _jsx(IconButton, Object.assign({ onClick: () => setShowOpenaiKey(!showOpenaiKey), edge: "end" }, { children: showOpenaiKey ? _jsx(VisibilityOff, {}) : _jsx(Visibility, {}) })) }))),
                            } }), testResults.openai && (_jsx(Alert, Object.assign({ severity: testResults.openai.valid ? "success" : "error", sx: { mb: 2 }, icon: testResults.openai.valid ? _jsx(CheckCircleIcon, {}) : _jsx(ErrorIcon, {}) }, { children: testResults.openai.valid ? "API key is valid ✓" : `Invalid API key: ${testResults.openai.error}` }))), _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 2 } }, { children: [_jsx(Button, Object.assign({ variant: "outlined", onClick: handleTestKey, disabled: !openaiKey || testing === "openai", startIcon: testing === "openai" ? _jsx(CircularProgress, { size: 16 }) : undefined }, { children: testing === "openai" ? "Testing..." : "Test Key" })), (apiKeys === null || apiKeys === void 0 ? void 0 : apiKeys.hasOpenaiKey) && (_jsx(Button, Object.assign({ variant: "outlined", color: "error", startIcon: _jsx(DeleteIcon, {}), onClick: handleDeleteKey }, { children: "Delete" })))] }))] }) })), _jsx(Box, Object.assign({ sx: { display: "flex", justifyContent: "flex-end" } }, { children: _jsx(Button, Object.assign({ variant: "contained", size: "large", onClick: handleSaveKeys, disabled: saving || !openaiKey, startIcon: saving ? _jsx(CircularProgress, { size: 16 }) : undefined }, { children: saving ? "Saving..." : "Save API Key" })) }))] })));
};
export default OpenAISettings;
